import React, { useCallback } from 'react'
import { useOutletContext } from 'react-router-dom'
import {
  Card,
  Typography,
  Flex,
  Select,
  Input,
  Button,
  Empty,
  Spin,
} from 'antd'
import { PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons'
import {
  type FilterRule,
  type FilterTarget,
  type FilterComparator,
  type Partner,
  filterTargetOptions,
  filterComparatorOptions,
  filterParameterOptions,
} from '@/data'
import { usePartnerSearch } from '@/utils/usePartnerSearch'
import type { TestEditorContext } from '../../index'

const { Title, Text } = Typography

let nextId = 1

function generateId(): string {
  return `filter-${Date.now()}-${nextId++}`
}

const affiliateMapOption = (p: Partner) => ({
  value: String(p.affiliate_id),
  label: `${p.name} (${p.affiliate_id})`,
})

const FilterValueField: React.FC<{
  rule: FilterRule
  onChangeValue: (value: string) => void
}> = ({ rule, onChangeValue }) => {
  const isAutocompletable =
    rule.target === 'url_parameter' &&
    (rule.parameter_name === 'aff_id' || rule.parameter_name === 'utm_campaign')

  const { options, loading, hasMore, onSearch, loadMore } = usePartnerSearch({
    mapOption: affiliateMapOption,
  })

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement
      const nearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 40
      if (nearBottom) loadMore()
    },
    [loadMore],
  )

  if (isAutocompletable) {
    return (
      <Select
        showSearch
        allowClear
        value={rule.value || undefined}
        onChange={(val: string) => onChangeValue(val ?? '')}
        onSearch={onSearch}
        onPopupScroll={handleScroll}
        placeholder="Search by name or ID..."
        style={{ width: '100%' }}
        loading={loading}
        suffixIcon={<SearchOutlined />}
        filterOption={false}
        options={options}
        notFoundContent={loading ? <Spin size="small" /> : 'No matches'}
        dropdownRender={(menu) => (
          <>
            {menu}
            {loading && options.length > 0 && (
              <Flex justify="center" style={{ padding: 8 }}>
                <Spin size="small" />
              </Flex>
            )}
            {!hasMore && options.length > 0 && (
              <Flex justify="center" style={{ padding: '4px 0' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  All results loaded
                </Text>
              </Flex>
            )}
          </>
        )}
      />
    )
  }

  return (
    <Input
      value={rule.value}
      onChange={(e) => onChangeValue(e.target.value)}
      placeholder={
        rule.target === 'url_path' ? 'e.g. /landing' : 'e.g. facebook'
      }
    />
  )
}

export const FiltersPage: React.FC = () => {
  const { triggers, setTriggers } = useOutletContext<TestEditorContext>()

  const addRule = () => {
    const newRule: FilterRule = {
      id: generateId(),
      target: 'url_parameter',
      parameter_name: '',
      comparator: 'equals',
      value: '',
      ordinal: triggers.length,
    }
    setTriggers([...triggers, newRule])
  }

  const removeRule = (id: string) => {
    setTriggers(triggers.filter((r) => r.id !== id))
  }

  const updateRule = (id: string, updates: Partial<FilterRule>) => {
    setTriggers(
      triggers.map((r) => {
        if (r.id !== id) return r
        const updated = { ...r, ...updates }
        // Clear parameter_name when switching away from url_parameter
        if (updates.target === 'url_path' || updates.target === 'website') {
          updated.parameter_name = ''
        }
        // Clear value when switching parameter_name
        if (
          updates.parameter_name !== undefined &&
          updates.parameter_name !== r.parameter_name
        ) {
          updated.value = ''
        }
        return updated
      }),
    )
  }

  return (
    <div>
      <Flex vertical gap="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Filters</Title>
          <Text type="secondary">
            Define which traffic should be targeted for this A/B test. Add
            filters to match visitors by URL path or URL parameters.
          </Text>
        </div>

        <Card
          title="Filter Rules"
          style={{ maxWidth: 800 }}
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addRule}
              size="small"
            >
              Add Filter
            </Button>
          }
        >
          {triggers.length === 0 ? (
            <Empty
              description={
                <Text type="secondary">
                  No filter rules defined. All traffic will be included in this
                  test.
                </Text>
              }
            >
              <Button type="dashed" icon={<PlusOutlined />} onClick={addRule}>
                Add Filter Rule
              </Button>
            </Empty>
          ) : (
            <Flex vertical gap="middle">
              {triggers.map((rule, index) => (
                <Card
                  key={rule.id}
                  size="small"
                  type="inner"
                  title={`Filter ${index + 1}`}
                  extra={
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeRule(rule.id)}
                      size="small"
                    />
                  }
                >
                  <Flex gap="small" wrap="wrap" align="end">
                    <Flex vertical gap={4} style={{ minWidth: 160 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Target
                      </Text>
                      <Select
                        value={rule.target}
                        onChange={(value: FilterTarget) =>
                          updateRule(rule.id, { target: value })
                        }
                        options={filterTargetOptions}
                        style={{ width: 160 }}
                      />
                    </Flex>

                    {rule.target === 'url_parameter' && (
                      <Flex vertical gap={4} style={{ minWidth: 160 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Parameter Name
                        </Text>
                        <Select
                          value={rule.parameter_name || undefined}
                          onChange={(value) =>
                            updateRule(rule.id, {
                              parameter_name: value,
                            })
                          }
                          placeholder="Select parameter"
                          style={{ width: 160 }}
                          options={[...filterParameterOptions]}
                        />
                      </Flex>
                    )}

                    <Flex vertical gap={4} style={{ minWidth: 170 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Comparator
                      </Text>
                      <Select
                        value={rule.comparator}
                        onChange={(value: FilterComparator) =>
                          updateRule(rule.id, { comparator: value })
                        }
                        options={filterComparatorOptions}
                        style={{ width: 170 }}
                      />
                    </Flex>

                    <Flex vertical gap={4} style={{ flex: 1, minWidth: 160 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Value
                      </Text>
                      <FilterValueField
                        rule={rule}
                        onChangeValue={(value) =>
                          updateRule(rule.id, { value })
                        }
                      />
                    </Flex>
                  </Flex>
                </Card>
              ))}

              {triggers.length > 0 && (
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                  All filters must match for a visitor to be included in the
                  test.
                </Text>
              )}
            </Flex>
          )}
        </Card>
      </Flex>
    </div>
  )
}
